// Reopen requests (BLD-22, instructor feedback pt 12): participants ask, the instructor accepts or declines.
import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, TextArea } from "./ui.tsx";
import { Modal } from "./dialog.tsx";
import { useToast } from "./toast.tsx";
import { ApiError, get, post } from "../lib/api.ts";
import { planKey, usePlan } from "../lib/plan.tsx";
import { GuardedLink } from "./plan-shell.tsx";
import type { ReopenRequest } from "../lib/types.ts";
import { formatInstantBR } from "../../shared/dates.ts";
import { initiativeLabel, krLabel, objectiveLabel } from "../../shared/numbering.ts";

export type ReopenTarget = { entity_type: ReopenRequest["entity_type"]; entity_id: string; title: string };

const NOUN = { initiative: "a iniciativa", objective: "o objetivo", key_result: "o KR" } as const;

export const requestLabel = (r: Pick<ReopenRequest, "entity_type" | "number">) =>
  r.entity_type === "initiative" ? initiativeLabel(r.number) : r.entity_type === "objective" ? objectiveLabel(r.number) : krLabel(r.number);

/** Shared mutation helper: refreshes everything that shows request state. */
function useReopenCommand() {
  const plan = usePlan();
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  async function run(path: string, body: object, success: string, headers?: Record<string, string>) {
    setBusy(true);
    try {
      await post(`/plans/${plan.id}${path}`, body, headers);
      toast("success", success);
      await qc.invalidateQueries({ queryKey: ["plan", plan.id] });
      return true;
    } catch (e) {
      if (e instanceof ApiError && e.code === "VERSION_CONFLICT") await qc.invalidateQueries({ queryKey: ["plan", plan.id] });
      toast("danger", e instanceof Error ? e.message : "Não foi possível concluir a operação.");
      return false;
    } finally {
      setBusy(false);
    }
  }
  return { run, busy };
}

/** Participant: ask the instructor to reopen a locked item, with a required reason. */
export function ReopenRequestDialog({ target, onClose }: { target: ReopenTarget; onClose: () => void }) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const { run, busy } = useReopenCommand();
  const key = useRef(crypto.randomUUID());
  async function send() {
    if (!reason.trim()) return setError("Explique ao instrutor o que precisa ser alterado.");
    const ok = await run("/reopen-requests", { entity_type: target.entity_type, entity_id: target.entity_id, reason: reason.trim() },
      "Pedido de reabertura enviado ao instrutor.", { "Idempotency-Key": key.current });
    if (ok) onClose();
  }
  return (
    <Modal open onOpenChange={(o) => !o && !busy && onClose()} title="Solicitar reabertura"
      description={<>O instrutor decide se reabre {NOUN[target.entity_type]} <b>{target.title}</b>. Enquanto isso, nada muda.</>}
      footer={<><Button variant="secondary" disabled={busy} onClick={onClose}>Cancelar</Button><Button loading={busy} onClick={() => void send()}>Enviar pedido</Button></>}>
      <TextArea label="O que precisa ser alterado?" required value={reason} error={error ?? undefined} maxLength={2000}
        onChange={(e) => { setReason(e.target.value); setError(null); }} placeholder="Ex.: incluir uma ação de mentoria e ajustar o prazo da ação 2" />
    </Modal>
  );
}

/** Instructor: accept (initiatives are reopened at once) or decline with a note. */
export function ReopenDecision({ request }: { request: ReopenRequest }) {
  const { run, busy } = useReopenCommand();
  const [declining, setDeclining] = useState(false);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const acceptLabel = request.entity_type === "initiative" ? "Reabrir agora" : "Aceitar pedido";
  return (
    <>
      <div className="flex flex-wrap gap-2">
        <Button loading={busy && !declining} onClick={() => void run(`/reopen-requests/${request.id}/accept`, { expected_version: request.version },
          request.entity_type === "initiative" ? "Iniciativa reaberta. O grupo já pode editar." : "Pedido aceito. Edite o item no Planejamento.")}>{acceptLabel}</Button>
        <Button variant="secondary" disabled={busy} onClick={() => setDeclining(true)}>Recusar</Button>
      </div>
      <Modal open={declining} onOpenChange={(o) => !o && !busy && setDeclining(false)} title="Recusar pedido de reabertura"
        footer={<><Button variant="secondary" disabled={busy} onClick={() => setDeclining(false)}>Voltar</Button><Button variant="danger" loading={busy} onClick={async () => {
          if (!note.trim()) return setError("Explique o motivo ao grupo.");
          if (await run(`/reopen-requests/${request.id}/decline`, { expected_version: request.version, note: note.trim() }, "Pedido recusado.")) setDeclining(false);
        }}>Recusar pedido</Button></>}>
        <TextArea label="Motivo (o grupo verá esta resposta)" required value={note} error={error ?? undefined} maxLength={2000}
          onChange={(e) => { setNote(e.target.value); setError(null); }} />
      </Modal>
    </>
  );
}

/**
 * Initiative page banner for validated / in-review work: explains the lock and offers the request (participants),
 * shows a pending request with the instructor's decision buttons, or the last answer.
 */
export function ReopenBanner({ target, request, canAsk, isInstructor, lockedMessage }: {
  target: ReopenTarget;
  request: ReopenRequest | null;
  canAsk: boolean;
  isInstructor: boolean;
  lockedMessage: string;
}) {
  const [asking, setAsking] = useState(false);
  const { run, busy } = useReopenCommand();
  const pending = request?.status === "pending" ? request : null;
  const last = request && !pending && request.decided_at ? request : null;
  return (
    <>
      {pending ? (
        <Alert tone="warn" title="Reabertura solicitada"
          action={isInstructor ? <ReopenDecision request={pending} />
            : canAsk ? <Button variant="secondary" loading={busy} onClick={() => void run(`/reopen-requests/${pending.id}/cancel`, { expected_version: pending.version }, "Pedido cancelado.")}>Cancelar pedido</Button> : undefined}>
          <p>{pending.requested_by_name} em {formatInstantBR(pending.requested_at)}: “{pending.reason}”</p>
          {!isInstructor && <p className="mt-1 text-xs">Aguardando a decisão do instrutor.</p>}
        </Alert>
      ) : (
        <Alert tone="info" action={canAsk && !isInstructor ? <Button variant="secondary" onClick={() => setAsking(true)}>Solicitar reabertura</Button> : undefined}>
          {lockedMessage}
          {last?.status === "declined" && <p className="mt-1 text-xs">Último pedido recusado por {last.decided_by_name} em {formatInstantBR(last.decided_at)}: “{last.decision_note}”</p>}
        </Alert>
      )}
      {asking && <ReopenRequestDialog target={target} onClose={() => setAsking(false)} />}
    </>
  );
}

/** Instructor queue (Revisões page): pending requests with links and decisions. */
export function ReopenRequestsPanel() {
  const plan = usePlan();
  const q = useQuery({
    queryKey: planKey(plan.id, "reopen-requests"),
    queryFn: async () => (await get<{ data: ReopenRequest[] }>(`/plans/${plan.id}/reopen-requests`)).data,
  });
  if (!q.data?.length) return null;
  const href = (r: ReopenRequest) => (r.initiative_id ? `/planos/${plan.id}/iniciativas/${r.initiative_id}` : `/planos/${plan.id}/planejamento?eixo=${r.axis_id}`);
  return (
    <section id="pedidos-reabertura" aria-labelledby="reopen-title" className="mb-6 rounded-panel border border-warn-ink/20 bg-warn/60 p-4">
      <h2 id="reopen-title" className="font-bold">Pedidos de reabertura ({q.data.length})</h2>
      <p className="text-sm text-muted">Participantes pedindo para alterar itens já validados ou em revisão.</p>
      <ul className="mt-3 space-y-3">
        {q.data.map((r) => (
          <li key={r.id} className="rounded-ctl border border-line bg-white p-3">
            <span className="flex items-center gap-1.5 text-[11px] font-bold tracking-wide text-muted uppercase">
              <span className="size-2 rounded-full" style={{ background: r.axis_color }} aria-hidden />{r.axis_name} · {requestLabel(r)}
            </span>
            <GuardedLink href={href(r)} className="font-semibold text-primary">{r.title}</GuardedLink>
            <p className="mt-1 text-sm">“{r.reason}”</p>
            <p className="mb-2 text-xs text-muted">{r.requested_by_name} · {formatInstantBR(r.requested_at)}</p>
            <ReopenDecision request={r} />
          </li>
        ))}
      </ul>
    </section>
  );
}
